import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchTaxSettings,
  saveTaxSettings,
  TAX_SETTINGS_PLACEHOLDER_ID,
  type TaxSettingsRecord,
} from '../services/taxSettings';

export default function TaxSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['operations', 'tax-settings'],
    queryFn: fetchTaxSettings,
  });

  const [defaultVatRateInput, setDefaultVatRateInput] = useState('10');
  const [defaultPricesIncludeVat, setDefaultPricesIncludeVat] = useState(true);
  const [foodVatRateInput, setFoodVatRateInput] = useState('10');
  const [foodPricesIncludeVat, setFoodPricesIncludeVat] = useState(true);
  const [drinkVatRateInput, setDrinkVatRateInput] = useState('10');
  const [drinkPricesIncludeVat, setDrinkPricesIncludeVat] = useState(true);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setDefaultVatRateInput(String(data.defaultVatRate));
      setDefaultPricesIncludeVat(data.defaultPricesIncludeVat);
      setFoodVatRateInput(String(data.foodVatRate));
      setFoodPricesIncludeVat(data.foodPricesIncludeVat);
      setDrinkVatRateInput(String(data.drinkVatRate));
      setDrinkPricesIncludeVat(data.drinkPricesIncludeVat);
    }
  }, [data]);

  const parsedRates = useMemo(() => {
    const parse = (value: string) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : NaN;
    };
    return {
      default: parse(defaultVatRateInput),
      food: parse(foodVatRateInput),
      drink: parse(drinkVatRateInput),
    };
  }, [defaultVatRateInput, foodVatRateInput, drinkVatRateInput]);

  const mutation = useMutation({
    mutationFn: saveTaxSettings,
    onSuccess: (saved) => {
      setStatusMessage('Vergi ayarları kaydedildi.');
      queryClient.setQueryData<TaxSettingsRecord>(
        ['operations', 'tax-settings'],
        saved,
      );
      setDefaultVatRateInput(String(saved.defaultVatRate));
      setDefaultPricesIncludeVat(saved.defaultPricesIncludeVat);
      setFoodVatRateInput(String(saved.foodVatRate));
      setFoodPricesIncludeVat(saved.foodPricesIncludeVat);
      setDrinkVatRateInput(String(saved.drinkVatRate));
      setDrinkPricesIncludeVat(saved.drinkPricesIncludeVat);
    },
    onError: (err: unknown) => {
      console.error('Failed to save tax settings', err);
      setStatusMessage('Ayarlar kaydedilemedi. Lütfen tekrar deneyin.');
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatusMessage(null);

    if (
      !Number.isFinite(parsedRates.default) ||
      parsedRates.default < 0 ||
      !Number.isFinite(parsedRates.food) ||
      parsedRates.food < 0 ||
      !Number.isFinite(parsedRates.drink) ||
      parsedRates.drink < 0
    ) {
      setStatusMessage('Tüm KDV oranları için geçerli değerler girin.');
      return;
    }

    const persistedId =
      data && data.id !== TAX_SETTINGS_PLACEHOLDER_ID ? data.id : undefined;

    mutation.mutate({
      id: persistedId,
      defaultVatRate: parsedRates.default,
      defaultPricesIncludeVat,
      foodVatRate: parsedRates.food,
      foodPricesIncludeVat,
      drinkVatRate: parsedRates.drink,
      drinkPricesIncludeVat,
    });
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Vergi Ayarları</h1>
          <p>Uygulamadaki fiyatların KDV oranını ve gösterim şeklini güncelleyin.</p>
        </div>
      </header>

      <section className="page-section">
        <div className="card">
          {isLoading ? (
            <p>Vergi ayarları yükleniyor…</p>
          ) : error ? (
            <p className="text-error">Ayarlar yüklenemedi. Lütfen sayfayı yenileyin.</p>
          ) : (
            <form className="form" onSubmit={handleSubmit}>
              <fieldset className="form-fieldset">
                <legend>Genel varsayılan</legend>
                <div className="form-field">
                  <label htmlFor="default-vat-rate">Genel KDV Oranı (%)</label>
                  <input
                    id="default-vat-rate"
                    type="number"
                    step="0.1"
                    min="0"
                    value={defaultVatRateInput}
                    onChange={(event) => setDefaultVatRateInput(event.target.value)}
                  />
                  <small>Türkiye için varsayılan oran %10&apos;dur. Bu değer, özel oran girilmeyen kategorilerde kullanılır.</small>
                </div>
                <div className="form-field checkbox">
                  <label htmlFor="default-prices-include-vat">
                    <input
                      id="default-prices-include-vat"
                      type="checkbox"
                      checked={defaultPricesIncludeVat}
                      onChange={(event) => setDefaultPricesIncludeVat(event.target.checked)}
                    />
                    <span>Fiyatlara KDV dahildir</span>
                  </label>
                </div>
              </fieldset>

              <fieldset className="form-fieldset">
                <legend>İçecekler</legend>
                <div className="form-field">
                  <label htmlFor="drink-vat-rate">İçecek KDV Oranı (%)</label>
                  <input
                    id="drink-vat-rate"
                    type="number"
                    step="0.1"
                    min="0"
                    value={drinkVatRateInput}
                    onChange={(event) => setDrinkVatRateInput(event.target.value)}
                  />
                  <small>Bu oran yalnızca ürün tipi &quot;drink&quot; olanlara uygulanır.</small>
                </div>
                <div className="form-field checkbox">
                  <label htmlFor="drink-prices-include-vat">
                    <input
                      id="drink-prices-include-vat"
                      type="checkbox"
                      checked={drinkPricesIncludeVat}
                      onChange={(event) => setDrinkPricesIncludeVat(event.target.checked)}
                    />
                    <span>İçecek fiyatlarına KDV dahildir</span>
                  </label>
                </div>
              </fieldset>

              <fieldset className="form-fieldset">
                <legend>Yiyecekler</legend>
                <div className="form-field">
                  <label htmlFor="food-vat-rate">Yiyecek KDV Oranı (%)</label>
                  <input
                    id="food-vat-rate"
                    type="number"
                    step="0.1"
                    min="0"
                    value={foodVatRateInput}
                    onChange={(event) => setFoodVatRateInput(event.target.value)}
                  />
                  <small>Pizza, sos, atıştırmalık gibi ürün tipleri bu oranı kullanır.</small>
                </div>
                <div className="form-field checkbox">
                  <label htmlFor="food-prices-include-vat">
                    <input
                      id="food-prices-include-vat"
                      type="checkbox"
                      checked={foodPricesIncludeVat}
                      onChange={(event) => setFoodPricesIncludeVat(event.target.checked)}
                    />
                    <span>Yiyecek fiyatlarına KDV dahildir</span>
                  </label>
                </div>
              </fieldset>

              {statusMessage && <p className="form-feedback">{statusMessage}</p>}

              <button
                type="submit"
                className="primary"
                disabled={
                  mutation.isPending ||
                  !Number.isFinite(parsedRates.default) ||
                  !Number.isFinite(parsedRates.food) ||
                  !Number.isFinite(parsedRates.drink)
                }
              >
                {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </form>
          )}
        </div>
        {data?.updatedAt && (
          <p className="hint">
            Son güncelleme:{' '}
            {new Date(data.updatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}
          </p>
        )}
      </section>
    </div>
  );
}
